import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { MatSort, Sort } from '@angular/material/sort';
import { Subject } from 'rxjs';
import { MatTableDataSource } from '@angular/material/table';
import { RecipeSearchContent, RecipeSearchResult } from '@shared/models/search.model';
import { DataRef, Domain, Language, Market } from '@shared/models/dataref.model';
import { PAGE_SIZE } from '@shared/constants/page.constant';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { DataTableComponent } from '@shared/components/data-table-manager/data-table/data-table.component';
import { DisplayData } from '@shared/components/data-table-manager/data-table-manager.component';
import { ColumnConfiguration } from '@shared/models/generic/column-configuration.model';
import { RefDataService } from '@services/ref-data.service';
import { ResourceMediaLite } from '@shared/models/generic/media/resource-media-lite.model';
import { TablePaginatorComponent } from '@shared/components/data-table-manager/table-paginator/table-paginator.component';

@Component({
  selector: 'app-communities-recipes-search-result',
  templateUrl: './communities-recipes-search-result.component.html',
  styleUrls: ['./communities-recipes-search-result.component.scss'],
  imports: [CommonModule, TranslateModule, DataTableComponent],
})
export class CommunitiesRecipesSearchResultComponent implements OnInit, OnChanges, OnDestroy {
  @Input()
    domains: Domain[] | null = [];
  @Input()
    searchResult: RecipeSearchResult | null | undefined;
  @Input()
    options: string[] = [];
  @Input()
    markets: Market[] = [];
  @Input()
    languages: Language[] = [];
  @Input()
    status: DataRef[] = [];

  @Output()
    pageChange: EventEmitter<TablePaginatorComponent> = new EventEmitter<TablePaginatorComponent>();
  @Output()
    sortChange: EventEmitter<Sort> = new EventEmitter<Sort>();

  @ViewChild(MatSort) matSort: MatSort | null = null;

  columnConfigs: ColumnConfiguration<RecipeSearchContent>[] = [];
  tableData = new MatTableDataSource<RecipeSearchContent>([]);
  displayInfo: DisplayData = {
    resultsMessage: '',
    dataLength: 0,
    noDataMessage: 'NO_RESULT_FOR_QUERY',
    totalPages: 0,
  };

  dataSource: MatTableDataSource<RecipeSearchContent> = new MatTableDataSource();
  pageSize = PAGE_SIZE;
  pageIndex = 0;
  totalElements = 0;
  hasNoResult = false;
  _sort?: Sort = { active: 'modificationDate', direction: 'desc' };

  private readonly destroy$ = new Subject<void>();

  constructor(private readonly datarefService: RefDataService) {}

  get sort(): Sort | undefined {
    return this._sort;
  }
  ngOnInit(): void {
    this.createColumnConfig();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.options?.currentValue) {
      this.options = changes.options.currentValue;
    }
    if (changes.searchResult?.currentValue) {
      if (changes.searchResult.currentValue.page) {
        this.enrichDatasource(changes.searchResult.currentValue);
      }
    }
  }

  enrichDatasource(result: RecipeSearchResult): void {
    const mappedContent = result.content?.map((recipe: RecipeSearchContent) => {
      const creationDate = new Date(recipe.creationDate);
      const modificationDate = new Date(recipe.modificationDate);

      const formatDate: (date: Date) => string = (date: Date) => {
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        return `${day}/${month}/${year} ${hours}:${minutes}`;
      };

      return {
        ...recipe,
        domainKey: recipe.domain,
        marketKey: recipe.market,
        langKey: recipe.lang,
        statusKey: recipe.status,
        domain: this.findDomain(recipe.domain)?.translatedName ?? recipe.domain,
        market: this.findMarket(recipe.market)?.translatedName ?? recipe.market,
        lang: this.findLanguage(recipe.lang)?.translatedName ?? recipe.lang,
        status: this.findRecipeStatus(recipe.status)?.translatedName ?? recipe.status,
        image: this.containsCover(recipe.resourceMedias) ? this.getCover(recipe.resourceMedias) : '../../../assets/images/no-picture.jpg',
        creationDate: `${recipe.creator || ''}\n${formatDate(creationDate)}`,
        modificationDate: `${recipe.modifier || ''}\n${formatDate(modificationDate)}`,
      };
    });
    this.dataSource = new MatTableDataSource<RecipeSearchContent>(mappedContent);
    this.dataSource.sort = this.matSort;
    this.totalElements = result.page.totalElements;
    this.hasNoResult = result.page.totalElements === 0;
    this.pageIndex = result.page.number;

    this.tableData.data = mappedContent;
    this.updateDisplayInfo(result.page.totalElements, result.page.totalPages);
  }

  findDomain(key: string): Domain | undefined {
    return (this.domains ?? []).find((domain) => domain.key === key);
  }

  findMarket(key: string): Market | undefined {
    return this.markets.find((market) => market.key === key);
  }

  findLanguage(key: string): Language | undefined {
    return this.languages.find((language) => language.key === key);
  }

  findRecipeStatus(key: string): DataRef | undefined {
    return this.status.find((RecipeStatus) => RecipeStatus.key === key);
  }

  containsCover(resourceMedias: ResourceMediaLite[]): boolean {
    return resourceMedias?.filter((resourceMedia) => resourceMedia.isCover).length === 1;
  }

  getCover(resourceMedias: ResourceMediaLite[]): string {
    const resourceMedia = resourceMedias.find((rm) => rm.isCover);
    return resourceMedia?.media?.thumbnail ?? '';
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  getRecipeDetailLink(event: MouseEvent, element: RecipeSearchContent): void {
    event.preventDefault();
    const link = this.generateLink(element);
    window.open(`${link}`, '_blank');
  }

  generateLink(element: RecipeSearchContent): string {
    // for /v1/variants recipes, remove leading slashes from functionalId
    const fid = element.identifier.functionalId.replace(/^\/+/, '');
    const sourceSystem = element.identifier.sourceSystem;
    const domain = (element as any).domainKey ?? element.domain;
    const market = (element as any).marketKey ?? element.market;
    const lang = (element as any).langKey ?? element.lang;

    return `/#/communities-recipies/${domain}/${market}/${lang}/moderation/${sourceSystem}/${fid}/preview`;

    // To return when details page will be ready
    // return `/communities-recipes/moderation/${element.identifier.sourceSystem}/${fid}`;
  }

  getStatusClass(recipeStatus: string): string {
    const validStatuses = ['VALIDATED_MARKET', 'VALIDATED_PARTNER'];
    const rejectedStatuses = ['REJECTED_PARTNER', 'REJECTED_MARKET'];

    if (validStatuses.includes(recipeStatus)) {
      return 'border-left: solid 4px #b0c641';
    }
    if (rejectedStatuses.includes(recipeStatus)) {
      return 'border-left: solid 4px #d00f37';
    }
    return 'border-left: solid 4px #fb9b30';
  }

  onFilterChanged(event: { sort: MatSort; paginator: TablePaginatorComponent }): void {
    // Always update _sort with current sort state from the event
    if (event.sort) {
      const newSort: Sort = {
        active: event.sort.active,
        direction: event.sort.direction,
      };

      // Check if sort has actually changed
      const sortChanged = event.sort.active !== this._sort?.active || event.sort.direction !== this._sort?.direction;

      // Update local sort state
      this._sort = newSort;

      // Emit sort change if it actually changed
      if (sortChanged) {
        this.sortChange.emit(newSort);
      }
    }

    // Handle page change (after sort handling to ensure sort is preserved)
    if (event.paginator && (event.paginator.pageIndex !== this.pageIndex || event.paginator.pageSize !== this.pageSize)) {
      this.pageIndex = event.paginator.pageIndex;
      this.pageSize = event.paginator.pageSize;
      this.pageChange.emit(event.paginator);
    }
  }

  private createColumnConfig(): void {
    this.columnConfigs = [
      {
        dataFieldName: 'image',
        name: 'image',
        headerTranslationKey: 'IMAGE',
        content: {
          type: 'string',
          value: (element: any) => element.image,
          isImage: true,
        },
        sortable: false,
      },
      {
        dataFieldName: 'title',
        name: 'title',
        headerTranslationKey: 'TITLE',
        content: {
          type: 'string',
          value: (element: RecipeSearchContent) => element.title,
        },
        sortable: true,
        linkCallback: (element: RecipeSearchContent) => {
          const event = new MouseEvent('click', { bubbles: true, cancelable: true });
          this.getRecipeDetailLink(event as any, element);
        },
      },
      {
        dataFieldName: 'domain',
        name: 'domain',
        headerTranslationKey: 'SEARCH_DOMAIN',
        content: {
          type: 'string',
          value: (element: RecipeSearchContent) =>
            this.datarefService.translateReference(this.findDomain(element.domain))?.translatedName ?? element.domain,
        },
        sortable: true,
      },
      {
        dataFieldName: 'market',
        name: 'market',
        headerTranslationKey: 'SEARCH_MARKET',
        content: {
          type: 'string',
          value: (element: RecipeSearchContent) =>
            this.datarefService.translateReference(this.findMarket(element.market))?.translatedName ?? element.market,
        },
        sortable: true,
      },
      {
        dataFieldName: 'lang',
        name: 'lang',
        headerTranslationKey: 'SEARCH_LANG',
        content: {
          type: 'string',
          value: (element: RecipeSearchContent) =>
            this.datarefService.translateReference(this.findLanguage(element.lang))?.translatedName ?? element.lang,
        },
        sortable: true,
      },
      {
        dataFieldName: 'creationDate',
        name: 'creationDate',
        headerTranslationKey: 'COMMUNITIES_RECIPES.TABLE_HEADER.CREATION_DATE',
        content: {
          type: 'string',
          value: (element: any) => element.creationDate,
        },
        sortable: true,
      },
      {
        dataFieldName: 'modificationDate',
        name: 'modificationDate',
        headerTranslationKey: 'COMMUNITIES_RECIPES.TABLE_HEADER.MODIFICATION_DATE',
        content: {
          type: 'string',
          value: (element: any) => element.modificationDate,
        },
        sortable: true,
      },
      {
        dataFieldName: 'status',
        name: 'status',
        headerTranslationKey: 'SEARCH_STATUS',
        content: {
          type: 'string',
          value: (element: RecipeSearchContent) =>
            this.datarefService.translateReference(this.findRecipeStatus(element.status))?.translatedName ?? element.status,
          isStatus: true,
          cellStyle: (element: RecipeSearchContent) => this.getStatusClass((element as any).statusKey),
        },
        sortable: true,
      },
    ];
  }

  private updateDisplayInfo(totalElements: number, totalPages: number): void {
    let resultsMessage = '';
    if (totalElements > 0) {
      resultsMessage = totalElements === 1 ? 'RECIPE_FOUND' : 'RECIPES_FOUND';
    }
    this.displayInfo = {
      resultsMessage,
      dataLength: totalElements,
      noDataMessage: 'NO_RESULT_FOR_QUERY',
      totalPages,
    };
  }
}
